import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Building2, Plus, Edit2, Trash2, CheckCircle, X, Save, Users, Star } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useAppContext } from '../../context/AppContext';
import { toast } from 'sonner';
export interface Department {
  id: string;
  name: string;
  description: string;
  color: string;
  staffIds: string[];
  isDefault: boolean;
  isActive: boolean;
}
const defaultDepartments: Department[] = [{
  id: 'dept_tech',
  name: 'Technical Support',
  description: 'Server issues, hosting setup, and technical queries',
  color: 'blue',
  staffIds: [],
  isDefault: true,
  isActive: true
}, {
  id: 'dept_billing',
  name: 'Billing & Accounts',
  description: 'Invoices, payments, and account management',
  color: 'emerald',
  staffIds: [],
  isDefault: false,
  isActive: true
}, {
  id: 'dept_sales',
  name: 'Sales',
  description: 'Pre-sales questions and custom quotes',
  color: 'purple',
  staffIds: [],
  isDefault: false,
  isActive: true
}, {
  id: 'dept_general',
  name: 'General Inquiry',
  description: 'Other questions and general support',
  color: 'slate',
  staffIds: [],
  isDefault: false,
  isActive: true
}];
const colorOptions = [{
  name: 'Blue',
  value: 'blue',
  bg: 'bg-blue-500',
  text: 'text-blue-500'
}, {
  name: 'Emerald',
  value: 'emerald',
  bg: 'bg-emerald-500',
  text: 'text-emerald-500'
}, {
  name: 'Purple',
  value: 'purple',
  bg: 'bg-purple-500',
  text: 'text-purple-500'
}, {
  name: 'Fox Orange',
  value: 'fox',
  bg: 'bg-fox-500',
  text: 'text-fox-500'
}, {
  name: 'Amber',
  value: 'amber',
  bg: 'bg-amber-500',
  text: 'text-amber-500'
}, {
  name: 'Rose',
  value: 'rose',
  bg: 'bg-rose-500',
  text: 'text-rose-500'
}, {
  name: 'Cyan',
  value: 'cyan',
  bg: 'bg-cyan-500',
  text: 'text-cyan-500'
}, {
  name: 'Slate',
  value: 'slate',
  bg: 'bg-slate-500',
  text: 'text-slate-500'
}];
export function DepartmentManager() {
  const {
    staff
  } = useAppContext();
  const [departments, setDepartments] = useState<Department[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingDept, setEditingDept] = useState<Department | null>(null);
  // Form states
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [color, setColor] = useState('blue');
  const [isActive, setIsActive] = useState(true);
  const [selectedStaff, setSelectedStaff] = useState<string[]>([]);
  useEffect(() => {
    loadDepartments();
  }, []);
  const loadDepartments = async () => {
    try {
      setIsLoading(true);
      const {
        data,
        error
      } = await supabase.from('site_settings').select('value').eq('key', 'departments').maybeSingle();
      if (error && error.code !== 'PGRST116') throw error;
      if (data?.value) {
        setDepartments(data.value as Department[]);
      } else {
        // Seed defaults
        setDepartments(defaultDepartments);
        await saveDepartments(defaultDepartments);
      }
    } catch (error) {
      console.error('Failed to load departments:', error);
      toast.error('Failed to load departments');
    } finally {
      setIsLoading(false);
    }
  };
  const saveDepartments = async (newDepts: Department[]) => {
    try {
      setIsSaving(true);
      const {
        error
      } = await supabase.from('site_settings').upsert({
        key: 'departments',
        value: newDepts
      }, {
        onConflict: 'key'
      });
      if (error) throw error;
      setDepartments(newDepts);
      toast.success('Departments saved successfully');
    } catch (error) {
      console.error('Failed to save departments:', error);
      toast.error('Failed to save departments');
    } finally {
      setIsSaving(false);
    }
  };
  const handleOpenModal = (dept?: Department) => {
    if (dept) {
      setEditingDept(dept);
      setName(dept.name);
      setDescription(dept.description);
      setColor(dept.color);
      setIsActive(dept.isActive);
      setSelectedStaff(dept.staffIds || []);
    } else {
      setEditingDept(null);
      setName('');
      setDescription('');
      setColor('blue');
      setIsActive(true);
      setSelectedStaff([]);
    }
    setIsModalOpen(true);
  };
  const handleSaveDept = async (e: React.FormEvent) => {
    e.preventDefault();
    const newDept: Department = {
      id: editingDept ? editingDept.id : `dept_${Date.now()}`,
      name,
      description,
      color,
      staffIds: selectedStaff,
      isDefault: editingDept ? editingDept.isDefault : departments.length === 0,
      isActive
    };
    let newDepts;
    if (editingDept) {
      newDepts = departments.map(d => d.id === editingDept.id ? newDept : d);
    } else {
      newDepts = [...departments, newDept];
    }
    await saveDepartments(newDepts);
    setIsModalOpen(false);
  };
  const handleDeleteDept = async (id: string) => {
    const dept = departments.find(d => d.id === id);
    if (dept?.isDefault) {
      toast.error('Cannot delete the default department. Set another as default first.');
      return;
    }
    if (window.confirm('Are you sure you want to delete this department? Tickets assigned to it will remain but lose their department tag.')) {
      const newDepts = departments.filter(d => d.id !== id);
      await saveDepartments(newDepts);
    }
  };
  const handleSetDefault = async (id: string) => {
    const newDepts = departments.map(d => ({
      ...d,
      isDefault: d.id === id
    }));
    await saveDepartments(newDepts);
  };
  const toggleStaff = (staffId: string) => {
    if (selectedStaff.includes(staffId)) {
      setSelectedStaff(selectedStaff.filter(id => id !== staffId));
    } else {
      setSelectedStaff([...selectedStaff, staffId]);
    }
  };
  if (isLoading) {
    return <div className="flex items-center justify-center h-64" data-id="element-389">
        <div className="w-8 h-8 border-2 border-fox-500/30 border-t-fox-500 rounded-full animate-spin" data-id="element-390" />
      </div>;
  }
  return <div className="space-y-8" data-id="element-391">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4" data-id="element-392">
        <div data-id="element-393">
          <h1 className="text-2xl font-bold text-white mb-2" data-id="element-394">
            Support Departments
          </h1>
          <p className="text-slate-400" data-id="element-395">
            Manage ticket routing and staff assignments by department.
          </p>
        </div>
        <button onClick={() => handleOpenModal()} className="flex items-center gap-2 bg-fox-600 hover:bg-fox-500 text-white px-5 py-2.5 rounded-xl font-medium transition-colors" data-id="element-396">
          <Plus size={18} data-id="element-397" />
          Add Department
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6" data-id="element-398">
        {departments.map(dept => {
        const colorConfig = colorOptions.find(c => c.value === dept.color) || colorOptions[0];
        const assignedStaff = staff.filter(s => dept.staffIds?.includes(s.id));
        return <motion.div key={dept.id} initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} className={`bg-slate-950 border ${dept.isDefault ? 'border-fox-500/50' : 'border-slate-800'} rounded-2xl p-6 relative overflow-hidden`} data-id="element-399">
              {!dept.isActive && <div className="absolute top-0 right-0 bg-slate-800 text-slate-400 text-xs font-bold px-3 py-1 rounded-bl-lg" data-id="element-400">
                  Inactive
                </div>}

              <div className="flex items-start justify-between mb-4" data-id="element-401">
                <div className="flex items-center gap-3" data-id="element-402">
                  <div className={`w-10 h-10 rounded-xl bg-${dept.color}-500/10 flex items-center justify-center shrink-0`} data-id="element-403">
                    <Building2 size={20} className={`text-${dept.color}-500`} data-id="element-404" />
                  </div>
                  <div data-id="element-405">
                    <h3 className="text-lg font-bold text-white flex items-center gap-2" data-id="element-406">
                      {dept.name}
                      {dept.isDefault && <Star size={14} className="text-fox-500 fill-fox-500" title="Default Department" data-id="element-407" />}
                    </h3>
                    <p className="text-sm text-slate-400 line-clamp-1" data-id="element-408">
                      {dept.description}
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-slate-900 rounded-xl p-4 mb-6" data-id="element-409">
                <div className="flex items-center justify-between text-sm mb-3" data-id="element-410">
                  <span className="text-slate-400 flex items-center gap-1.5" data-id="element-411">
                    <Users size={16} data-id="element-412" /> Assigned Staff
                  </span>
                  <span className="text-white font-medium" data-id="element-413">
                    {assignedStaff.length}
                  </span>
                </div>
                <div className="flex flex-wrap gap-1.5" data-id="element-414">
                  {assignedStaff.length === 0 ? <span className="text-xs text-slate-500" data-id="element-415">
                      No staff assigned
                    </span> : assignedStaff.slice(0, 5).map(s => <span key={s.id} className="text-xs bg-slate-800 text-slate-300 px-2 py-1 rounded-md" title={s.name} data-id="element-416">
                        {s.name.split(' ')[0]}
                      </span>)}
                  {assignedStaff.length > 5 && <span className="text-xs bg-slate-800 text-slate-400 px-2 py-1 rounded-md" data-id="element-417">
                      +{assignedStaff.length - 5}
                    </span>}
                </div>
              </div>

              <div className="flex items-center justify-between pt-4 border-t border-slate-800" data-id="element-418">
                {!dept.isDefault ? <button onClick={() => handleSetDefault(dept.id)} className="text-xs font-medium text-slate-400 hover:text-fox-500 transition-colors" data-id="element-419">
                    Set as Default
                  </button> : <span className="text-xs font-medium text-fox-500" data-id="element-420">
                    Default Department
                  </span>}

                <div className="flex items-center gap-2" data-id="element-421">
                  <button onClick={() => handleOpenModal(dept)} className="p-2 text-slate-400 hover:text-white bg-slate-900 rounded-lg border border-slate-800 transition-colors" data-id="element-422">
                    <Edit2 size={14} data-id="element-423" />
                  </button>
                  <button onClick={() => handleDeleteDept(dept.id)} disabled={dept.isDefault} className="p-2 text-slate-400 hover:text-red-500 bg-slate-900 rounded-lg border border-slate-800 transition-colors disabled:opacity-30 disabled:cursor-not-allowed" data-id="element-424">
                    <Trash2 size={14} data-id="element-425" />
                  </button>
                </div>
              </div>
            </motion.div>;
      })}
      </div>

      {/* Modal */}
      <AnimatePresence data-id="element-426">
        {isModalOpen && <>
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40" onClick={() => setIsModalOpen(false)} data-id="element-427" />
            <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} exit={{
          opacity: 0,
          scale: 0.95
        }} className="fixed inset-0 z-50 flex items-center justify-center p-4 overflow-y-auto" data-id="element-428">
              <div className="bg-slate-900 border border-slate-800 rounded-2xl w-full max-w-xl shadow-2xl my-8" data-id="element-429">
                <div className="flex items-center justify-between p-6 border-b border-slate-800 sticky top-0 bg-slate-900 rounded-t-2xl z-10" data-id="element-430">
                  <h2 className="text-xl font-bold text-white" data-id="element-431">
                    {editingDept ? 'Edit Department' : 'New Department'}
                  </h2>
                  <button onClick={() => setIsModalOpen(false)} className="text-slate-400 hover:text-white" data-id="element-432">
                    <X size={24} data-id="element-433" />
                  </button>
                </div>

                <form onSubmit={handleSaveDept} className="p-6 space-y-6" data-id="element-434">
                  <div className="space-y-2" data-id="element-435">
                    <label className="text-sm font-medium text-slate-300" data-id="element-436">
                      Department Name
                    </label>
                    <input type="text" required value={name} onChange={e => setName(e.target.value)} className="w-full bg-slate-950 border border-slate-800 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-fox-500" placeholder="e.g. Technical Support" data-id="element-437" />
                  </div>

                  <div className="space-y-2" data-id="element-438">
                    <label className="text-sm font-medium text-slate-300" data-id="element-439">
                      Description
                    </label>
                    <textarea required value={description} onChange={e => setDescription(e.target.value)} rows={2} className="w-full bg-slate-950 border border-slate-800 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-fox-500 resize-none" placeholder="What types of issues does this department handle?" data-id="element-440" />
                  </div>

                  <div className="space-y-2" data-id="element-441">
                    <label className="text-sm font-medium text-slate-300" data-id="element-442">
                      Badge Color
                    </label>
                    <div className="flex flex-wrap gap-3" data-id="element-443">
                      {colorOptions.map(c => <button key={c.value} type="button" onClick={() => setColor(c.value)} className={`w-8 h-8 rounded-full ${c.bg} ${color === c.value ? 'ring-2 ring-white ring-offset-2 ring-offset-slate-900' : 'opacity-70 hover:opacity-100'} transition-all`} title={c.name} data-id="element-444" />)}
                    </div>
                  </div>

                  <div className="space-y-3" data-id="element-445">
                    <label className="text-sm font-medium text-slate-300 flex items-center justify-between" data-id="element-446">
                      <span data-id="element-447">Assign Staff</span>
                      <span className="text-xs text-slate-500" data-id="element-448">
                        {selectedStaff.length} selected
                      </span>
                    </label>
                    <div className="bg-slate-950 border border-slate-800 rounded-xl p-4 max-h-48 overflow-y-auto space-y-2" data-id="element-449">
                      {staff.length === 0 ? <p className="text-sm text-slate-500 text-center py-2" data-id="element-450">
                          No staff members found.
                        </p> : staff.map(member => <label key={member.id} className="flex items-center gap-3 p-2 hover:bg-slate-900 rounded-lg cursor-pointer transition-colors" data-id="element-451">
                            <input type="checkbox" checked={selectedStaff.includes(member.id)} onChange={() => toggleStaff(member.id)} className="w-4 h-4 rounded border-slate-700 bg-slate-900 text-fox-600 focus:ring-fox-500 focus:ring-offset-slate-950" data-id="element-452" />
                            <div data-id="element-453">
                              <div className="text-sm font-medium text-white" data-id="element-454">
                                {member.name}
                              </div>
                              <div className="text-xs text-slate-500 capitalize" data-id="element-455">
                                {member.role}
                              </div>
                            </div>
                          </label>)}
                    </div>
                  </div>

                  <div className="flex items-center gap-3" data-id="element-456">
                    <input type="checkbox" id="isActive" checked={isActive} onChange={e => setIsActive(e.target.checked)} className="w-4 h-4 rounded border-slate-700 bg-slate-900 text-fox-600 focus:ring-fox-500 focus:ring-offset-slate-900" data-id="element-457" />
                    <label htmlFor="isActive" className="text-sm font-medium text-slate-300" data-id="element-458">
                      Department is active and accepting tickets
                    </label>
                  </div>

                  <div className="pt-4 border-t border-slate-800 flex justify-end gap-3" data-id="element-459">
                    <button type="button" onClick={() => setIsModalOpen(false)} className="px-5 py-2.5 text-sm font-medium text-slate-400 hover:text-white transition-colors" data-id="element-460">
                      Cancel
                    </button>
                    <button type="submit" disabled={isSaving} className="flex items-center gap-2 bg-fox-600 hover:bg-fox-500 text-white px-6 py-2.5 rounded-xl font-medium transition-all disabled:opacity-50" data-id="element-461">
                      {isSaving ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" data-id="element-462" /> : <Save size={18} data-id="element-463" />}
                      Save Department
                    </button>
                  </div>
                </form>
              </div>
            </motion.div>
          </>}
      </AnimatePresence>
    </div>;
}